import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";

import { useMount } from "@sportal/cdk/hooks";
import { NotMobile } from "../../common/Responsive";
import { getTimezone } from "../../store/account";
import { getIs24hourFormat } from "../../store/attributes";
import { XAxis } from "./XAxis";
import { useReportContext } from "./ReportProvider";
import {
  ActivityBars,
  BlocksBars,
  ChartMessage,
  SSBars,
} from "./components/ChartComponents";
import { BarChartTooltip } from "./tooltip/BarChartTooltip";
import { useBarchartTooltip } from "./useBarchartTooltip";
import { isPI, isSS } from "../../store/info";
import { useIntlTranslate } from "../../hooks";

const HEIGHT = 110;

const BarChartComponent = ({ container }) => {
  const translate = useIntlTranslate();

  const tz = useSelector(getTimezone);
  const is24 = useSelector(getIs24hourFormat);
  const isSafetyMode = useSelector(isSS);
  const isPiOnlyMode = useSelector(isPI);

  const translations = {
    SSPI: {
      label: `${translate("internet_activity")}, ${translate(
        "blocked_activity"
      )} and ${translate("malware_and_phishing_blocks")}`,
      description: `${translate(
        "internet_activity_bar_chart_description"
      )} ${translate("blocked_activity_bar_chart_description")} ${translate(
        "malware_and_phishing_blocks_bar_chart_description"
      )}`,
    },
    SS: {
      label: `${translate("internet_activity")} and ${translate(
        "malware_and_phishing_blocks"
      )}`,
      description: `${translate(
        "internet_activity_bar_chart_description"
      )} ${translate("malware_and_phishing_blocks_bar_chart_description")}`,
    },
    PI: {
      label: `${translate("internet_activity")} and ${translate(
        "blocked_activity"
      )}`,
      description: `${translate(
        "internet_activity_bar_chart_description"
      )} ${translate("blocked_activity_bar_chart_description")}`,
    },
  };

  const activeMode =
    isSafetyMode || isPiOnlyMode ? (isSafetyMode ? "SS" : "PI") : "SSPI";
  const numberOfCategories = isSafetyMode || isPiOnlyMode ? 2 : 3;

  const { duration } = useReportContext();

  const labelId = "homePageBarChartLabelId";

  return (
    <div>
      <svg
        role="img"
        aria-label={translate("bar_chart_area_label", {
          numberOfCategories,
          categories: translations[activeMode].label,
        })}
        aria-describedby={labelId}
        focusable="false"
      >
        <XAxis tz={tz} is24={is24} topOffset={HEIGHT} />
        <ActivityBars />
        <BlocksBars />
        <SSBars />
        <ChartMessage />
      </svg>
      <NotMobile>
        <BarChartTooltip
          container={container}
          is24={is24}
          tz={tz}
          duration={duration}
        />
      </NotMobile>
      <div aria-hidden={true} className="sr-only" id={labelId}>
        <FormattedMessage
          id={"bar_chart_area_description"}
          values={{
            numberOfCategories,
            categories: translations[activeMode].description,
          }}
        />
      </div>
    </div>
  );
};

function useBarchartTooltipCleanup() {
  const unselect = useBarchartTooltip((state) => state.unselect);

  useMount(() => unselect);
}

export const BarChart = () => {
  const { barchartClasses } = useReportContext();
  const container = useRef(null);

  useBarchartTooltipCleanup();

  return (
    <div className={barchartClasses} ref={container}>
      <BarChartComponent container={container} />
    </div>
  );
};
